import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge, Button, Card, Modal } from '@/components';
import { useUrlParam } from '@/hooks/urlState';

import { SUBNAV_RESET, VIEW_RESET } from './urlSections';

import { AttentionRidersView } from './components/AttentionRidersView';
import { MetricsView } from './components/MetricsView';
import { ReconciliationView } from './components/ReconciliationView';
import { TableView } from './components/TableView';
import { AGG, Avatar, Panel, StatCell } from './components/shared';
import { BadgeTone } from '@/components/Badge/Badge';
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  SiAggregateStatus,
  SiConfirmationStatus,
  SiRiderDayOff,
  SiSchedule,
  SiScheduleRosterSummary,
  SiSlotDetail,
  SiSlotRider,
  SiSlotSummary,
  scheduledInstantAPI,
} from '@/services/api/scheduled-instant';

import CreateScheduleModal from './components/CreateScheduleModal';
import CreateSlotModal from './components/CreateSlotModal';
import { ClientPicker } from './components/pickers';
import DayOffEditorModal from './components/DayOffEditorModal';
import DeactivateScheduleModal from './components/DeactivateScheduleModal';
import EditSlotModal from './components/EditSlotModal';
import ScheduleDetailDrawer from './components/ScheduleDetailDrawer';
import StandbyModal from './components/StandbyModal';

type Tab = 'slot' | 'master' | 'dayoff' | 'metrics' | 'recon';
type View = 'cal' | 'tbl' | 'attention';

const TABS = ['slot', 'master', 'dayoff', 'metrics', 'recon'] as const;
const VIEWS = ['cal', 'tbl', 'attention'] as const;


const STATUSES: SiConfirmationStatus[] = [
  'PENDING',
  'CONFIRMED',
  'NO_RESPONSE',
  'NEEDS_REPLACEMENT',
  'REPLACED',
  'COMPLETED',
  'FAILED',
];

// Mirror of the backend RANK (D17): a lower rank = backward = reason required.
const RANK: Record<SiConfirmationStatus, number> = {
  PENDING: 0,
  CONFIRMED: 1,
  NO_RESPONSE: 1,
  NEEDS_REPLACEMENT: 2,
  REPLACED: 3,
  COMPLETED: 4,
  FAILED: 4,
};

const STATUS_TONE: Record<SiConfirmationStatus, BadgeTone> = {
  PENDING: 'amber',
  CONFIRMED: 'success',
  NO_RESPONSE: 'danger',
  NEEDS_REPLACEMENT: 'danger',
  REPLACED: 'primary',
  COMPLETED: 'neutral',
  FAILED: 'rose',
};

// Human-friendly labels — the dropdown and toasts show these, never the raw enum.
const STATUS_LABEL: Record<SiConfirmationStatus, string> = {
  PENDING: 'Menunggu',
  CONFIRMED: 'Konfirmasi',
  NO_RESPONSE: 'Tak merespons',
  NEEDS_REPLACEMENT: 'Perlu pengganti',
  REPLACED: 'Diganti',
  COMPLETED: 'Selesai',
  FAILED: 'Gagal',
};

// Tinted-pill styling for the status <select>, keyed by the badge tone so the
// control reads as an editable status chip (matches the Badge colors elsewhere).
const STATUS_SELECT_TONE: Record<string, string> = {
  success: 'bg-green-50 border-green-300 text-green-700',
  amber: 'bg-amber-50 border-amber-300 text-amber-800',
  danger: 'bg-red-50 border-red-300 text-red-700',
  primary:
    'bg-(--theme-accent-alpha-10) border-(--theme-accent-alpha-24) text-accent',
  neutral: 'bg-gray-100 border-gray-300 text-gray-700',
  rose: 'bg-rose-50 border-rose-300 text-rose-700',
};

const DAY_LABELS = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'];

// Calendar slot block: background/text = response status; the BORDER encodes
// origin (solid = from master jadwal, dashed = mandiri). Kept separate from AGG
// (whose `block` carries a status-colored left border) so the two signals don't
// fight. Background unchanged from the response-status palette.
const CAL_STATUS_TONE: Record<SiAggregateStatus, string> = {
  confirmed: 'bg-green-50 text-green-800',
  pending: 'bg-amber-50 text-amber-800',
  no_response: 'bg-red-50 text-red-800',
  unstaffed: 'bg-amber-50/50 text-amber-700 italic',
};

const mondayOf = (d: dayjs.Dayjs) => {
  const iso = d.day() === 0 ? 7 : d.day();
  return d.subtract(iso - 1, 'day').startOf('day');
};

const ScheduledInstantPage: React.FC = () => {
  const [tab, setTab] = useUrlParam<Tab>('tab', 'slot', {
    allowed: TABS,
    history: 'push',
    resetParams: SUBNAV_RESET,
  });

  return (
    <div className="p-6 font-sans">
      <header className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
          Hub Ops
        </p>
        <h1 className="text-2xl font-bold tracking-tight text-ink mt-1">
          Scheduled Instant
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Jadwal rider harian, konfirmasi &amp; pengganti · Phase 1
        </p>
      </header>

      {/* segmented sub-nav */}
      <div className="inline-flex gap-1 bg-tint p-1 rounded-gsm-md mb-6">
        {(
          [
            ['slot', 'Slot Harian'],
            ['master', 'Master Jadwal'],
            ['dayoff', 'Day Off Rider'],
            ['metrics', 'Reliabilitas'],
            ['recon', 'Rekonsiliasi'],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-gsm transition-colors ${
              tab === key
                ? 'bg-white text-ink shadow-sm'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'slot' && <SlotHarian />}
      {tab === 'master' && <MasterJadwal />}
      {tab === 'dayoff' && <DayOff />}
      {tab === 'metrics' && <MetricsView />}
      {tab === 'recon' && <ReconciliationView />}
    </div>
  );
};

// ─── Slot Harian ────────────────────────────────────────────────────────

const SlotHarian: React.FC = () => {
  const [view, setView] = useUrlParam<View>('view', 'cal', {
    allowed: VIEWS,
    resetParams: VIEW_RESET,
  });
  // Screen 15's day. Empty = today, so the base URL stays clean.
  const [attentionDate, setAttentionDate] = useUrlParam<string>('adate', '');
  const [client, setClient] = useUrlParam<string>('client', '');
  // Provider id backs the client filter dropdown's selected value; the client
  // NAME is what listSlots filters on (and what persists in the URL), so the
  // id is local-only and just drives the picker's highlighted option.
  const [filterProviderId, setFilterProviderId] = useState<number | ''>('');
  // Week is stored in the URL as the Monday's YYYY-MM-DD; empty = current week.
  const [weekParam, setWeekParam] = useUrlParam<string>('week', '');
  const weekStart = useMemo(
    () => (weekParam ? mondayOf(dayjs(weekParam)) : mondayOf(dayjs())),
    [weekParam]
  );
  const setWeekStart = useCallback(
    (d: dayjs.Dayjs) => {
      const monday = mondayOf(d);
      // Clear the param when it's the current week so the base URL stays clean.
      setWeekParam(
        monday.isSame(mondayOf(dayjs()), 'day')
          ? ''
          : monday.format('YYYY-MM-DD')
      );
    },
    [setWeekParam]
  );
  const [slots, setSlots] = useState<SiSlotSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editSlotId, setEditSlotId] = useState<string | null>(null);
  // Calendar click opens a read-only detail first; the detail has an Edit button.
  const [detailSlotId, setDetailSlotId] = useState<string | null>(null);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day')),
    [weekStart]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSlots(
        await scheduledInstantAPI.listSlots({
          start_date: weekStart.format('YYYY-MM-DD'),
          end_date: weekStart.add(6, 'day').format('YYYY-MM-DD'),
          client: client || undefined,
        })
      );
    } catch (e: any) {
      setError(e.message || 'Gagal memuat slot');
    } finally {
      setLoading(false);
    }
  }, [weekStart, client]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const s = { total: slots.length, unstaffed: 0, attention: 0, confirmed: 0 };
    for (const x of slots) {
      if (x.aggregateStatus === 'unstaffed') s.unstaffed++;
      else if (x.aggregateStatus === 'no_response') s.attention++;
      else if (x.aggregateStatus === 'confirmed') s.confirmed++;
    }
    return s;
  }, [slots]);

  // Screen 15 takes over the whole Slot Harian body: it's a focused triage view
  // reached from the "Perlu Perhatian" card, not a panel beside the calendar.
  if (view === 'attention') {
    return (
      <AttentionRidersView
        date={attentionDate || dayjs().format('YYYY-MM-DD')}
        onDateChange={(d) =>
          setAttentionDate(d === dayjs().format('YYYY-MM-DD') ? '' : d)
        }
        client={client}
        onClientChange={setClient}
        onBack={() => setView('cal')}
      />
    );
  }

  return (
    <div>
      {/* summary strip — compact + consistent, semantic via number color */}
      <Panel className="mb-5">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-100">
          <StatCell label="Total Slot" value={summary.total} tone="ink" />
          <StatCell
            label="Belum Ada Rider"
            value={summary.unstaffed}
            tone="amber"
          />
          <button
            type="button"
            onClick={() => setView('attention')}
            className="text-left hover:bg-red-50/60 transition-colors group"
            title="Lihat rider yang belum merespons"
          >
            <StatCell
              label="Perlu Perhatian"
              value={summary.attention}
              tone="red"
              hint="Lihat rider belum merespons ›"
            />
          </button>
          <StatCell
            label="Terkonfirmasi"
            value={summary.confirmed}
            tone="green"
          />
        </div>
      </Panel>

      {/* toolbar */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {/* left: today + week nav */}
        <Button
          variant="ghost"
          size="small"
          onClick={() => setWeekStart(mondayOf(dayjs()))}
        >
          Hari ini
        </Button>
        <Button
          variant="white"
          size="small"
          onClick={() => setWeekStart(weekStart.subtract(7, 'day'))}
        >
          ‹
        </Button>
        <span className="text-sm font-semibold text-ink tabular min-w-[150px] text-center">
          {weekStart.format('DD MMM')} –{' '}
          {weekStart.add(6, 'day').format('DD MMM YYYY')}
        </span>
        <Button
          variant="white"
          size="small"
          onClick={() => setWeekStart(weekStart.add(7, 'day'))}
        >
          ›
        </Button>

        {/* right: view toggle + filter + add */}
        <div className="ml-auto inline-flex items-center rounded-gsm-md border border-gray-200 bg-tint p-0.5">
          {(
            [
              ['cal', '📅 Calendar'],
              ['tbl', '☰ Table'],
            ] as [View, string][]
          ).map(([v, label]) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-gsm transition-colors ${
                view === v
                  ? 'bg-white text-ink shadow-sm'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="w-60">
          <ClientPicker
            open
            providerId={filterProviderId}
            clientName={client}
            placeholder="Filter klien…"
            onChange={(sel) => {
              setFilterProviderId(sel ? sel.providerId : '');
              setClient(sel ? sel.clientName : '');
            }}
          />
        </div>

        <Button
          variant="primary"
          size="small"
          onClick={() => setShowCreate(true)}
        >
          <i className="ri-add-line mr-1" /> Tambah Slot
        </Button>
      </div>

      {loading && <SkeletonBlock />}

      {!loading && error && (
        <Card className="border-red-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-red-700">{error}</p>
            <Button variant="outline-danger" size="small" onClick={load}>
              Coba lagi
            </Button>
          </div>
        </Card>
      )}

      {!loading && !error && slots.length === 0 && (
        <Card>
          <div className="flex flex-col items-center text-center py-12">
            <div className="w-14 h-14 rounded-full bg-(--theme-accent-alpha-10) flex items-center justify-center text-2xl mb-3">
              🗓️
            </div>
            <h3 className="text-base font-bold text-ink">Belum ada slot</h3>
            <p className="text-sm text-gray-500 max-w-xs mt-1 mb-4">
              Tidak ada slot untuk minggu &amp; filter ini.
            </p>
            <Button
              variant="primary"
              size="small"
              onClick={() => setShowCreate(true)}
            >
              + Tambah Slot
            </Button>
          </div>
        </Card>
      )}

      {!loading && !error && slots.length > 0 && view === 'cal' && (
        <CalendarView days={days} slots={slots} onOpenSlot={setEditSlotId} />
      )}
      {!loading && !error && slots.length > 0 && view === 'tbl' && (
        <TableView
          slots={slots}
          onChanged={load}
          onEditSlot={setEditSlotId}
          renderRiderPanel={(slotId) => (
            <SlotRiderPanel
              slotId={slotId}
              onChanged={load}
              onEditSlot={setEditSlotId}
            />
          )}
        />
      )}

      <CreateSlotModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={load}
        defaultDate={weekStart.format('YYYY-MM-DD')}
      />

      {/* Detail-first: calendar click opens this read-only detail; its
          "Edit Slot" button hands off to EditSlotModal. */}
      <Modal
        isOpen={!!detailSlotId}
        onClose={() => setDetailSlotId(null)}
        icon="ri-calendar-event-line"
        iconTone="primary"
        title="Detail Slot"
        width="xl"
        padded={false}
      >
        {detailSlotId && (
          <SlotRiderPanel
            slotId={detailSlotId}
            onChanged={load}
            indented={false}
            onEditSlot={(id) => {
              setDetailSlotId(null);
              setEditSlotId(id);
            }}
          />
        )}
      </Modal>

      <EditSlotModal
        slotId={editSlotId}
        onClose={() => setEditSlotId(null)}
        onSaved={load}
      />
    </div>
  );
};

const SkeletonBlock: React.FC = () => (
  <Panel className="p-4">
    <div className="animate-pulse space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-9 bg-tint rounded-gsm" />
      ))}
    </div>
  </Panel>
);

const CalendarView: React.FC<{
  days: dayjs.Dayjs[];
  slots: SiSlotSummary[];
  onOpenSlot: (slotId: string) => void;
}> = ({ days, slots, onOpenSlot }) => {
  // Always render the full day (00:00–23:00), not just hours that happen to
  // have a slot, so every start time is selectable/visible across the range.
  const hours = useMemo(
    () => Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')),
    []
  );
  const today = dayjs().format('YYYY-MM-DD');

  return (
    <Panel>
      <div className="overflow-x-auto">
        <div
          className="grid text-xs min-w-[720px]"
          style={{ gridTemplateColumns: `52px repeat(7, 1fr)` }}
        >
        <div className="bg-gray-50 border-b border-r border-gray-100" />
        {days.map((d, i) => {
          const isToday = d.format('YYYY-MM-DD') === today;
          return (
            <div
              key={i}
              className={`p-2 text-center border-b border-r border-gray-100 ${
                isToday ? 'bg-(--theme-accent-alpha-10)' : 'bg-gray-50'
              }`}
            >
              <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">
                {DAY_LABELS[i]}
              </div>
              <div
                className={`text-xs font-bold ${
                  isToday ? 'text-accent' : 'text-ink'
                }`}
              >
                {d.format('DD')}
              </div>
            </div>
          );
        })}

        {hours.map((h) => (
          <React.Fragment key={h}>
            <div className="p-2 text-[10px] font-semibold text-gray-500 text-right border-b border-r border-gray-100 tabular">
              {h}:00
            </div>
            {days.map((d, di) => {
              const cell = slots.filter(
                (s) =>
                  s.slot.scheduleDate === d.format('YYYY-MM-DD') &&
                  s.slot.windowStartTime.slice(0, 2) === h
              );
              return (
                <div
                  key={di}
                  className="min-h-[46px] p-1 border-b border-r border-gray-100 flex flex-col gap-1"
                >
                  {cell.map((s) => {
                    const meta = AGG[s.aggregateStatus];
                    // Origin: from master jadwal (solid border + 🔗) vs mandiri
                    // (dashed border + 📌). Edited = recurring + overridden today.
                    const isRecurring = s.slot.scheduleId != null;
                    const isEdited = isRecurring && !!s.slot.editedAt;
                    const originBorder = isRecurring
                      ? 'border-[1.5px] border-l-[3px] border-primary'
                      : 'border-[1.5px] border-l-[3px] border-dashed border-gray-400';
                    const utamaCount = s.riderNames.length;
                    return (
                      <div
                        key={s.slot.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => onOpenSlot(s.slot.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ')
                            onOpenSlot(s.slot.id);
                        }}
                        className={`relative rounded-gsm px-1.5 py-1 leading-tight cursor-pointer transition-shadow hover:shadow-sm ${CAL_STATUS_TONE[s.aggregateStatus]} ${originBorder}`}
                        title={`${s.slot.clientName} ${s.slot.windowStartTime}-${s.slot.windowEndTime} · ${meta.label} · ${
                          isRecurring ? 'dari master jadwal' : 'slot mandiri'
                        }${isEdited ? ' · diedit' : ''} · klik untuk lihat detail`}
                      >
                        <b className="block truncate">
                          <span className="mr-0.5">
                            {isRecurring ? '🔗' : '📌'}
                          </span>
                          {s.slot.clientName}{' '}
                          {s.slot.windowStartTime.slice(0, 2)}-
                          {s.slot.windowEndTime.slice(0, 2)}
                        </b>
                        {isEdited && (
                          <span
                            className="absolute -top-1.5 -right-1 z-10 whitespace-nowrap rounded-full bg-accent px-1.5 py-px text-[8px] font-bold text-white shadow"
                            title="Tanggal ini diedit manual"
                          >
                            ✎ Diedit
                          </span>
                        )}
                        <span className="flex flex-col gap-0.5">
                          {utamaCount > 0 ? (
                            <>
                              {s.riderNames.slice(0, 2).map((name, i) => (
                                <span
                                  key={i}
                                  className="block truncate font-medium"
                                >
                                  {name}
                                </span>
                              ))}
                              {utamaCount > 2 && (
                                <span className="block truncate font-semibold opacity-90">
                                  +{utamaCount - 2} lainnya ({utamaCount} rider)
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="truncate">
                              Belum ada rider utama
                            </span>
                          )}
                          {s.dayOffCount > 0 && (
                            <span
                              className="inline-flex items-center gap-0.5 text-amber-600 font-semibold"
                              title={`${s.dayOffCount} rider day-off`}
                            >
                              <i className="ri-moon-line text-[10px]" />
                              {s.dayOffCount}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </React.Fragment>
        ))}
        </div>
      </div>
    </Panel>
  );
};

const SlotRiderPanel: React.FC<{
  slotId: string;
  onChanged: () => void;
  onEditSlot?: (slotId: string) => void;
  /** Table-row expansion indents left (pl-10) to clear the chevron; the modal
   *  wants symmetric body padding instead. Defaults to the table look. */
  indented?: boolean;
}> = ({ slotId, onChanged, onEditSlot, indented = true }) => {
  const [detail, setDetail] = useState<SiSlotDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // Reason-on-backward flow (D17): a backward move opens a modal instead of a
  // native window.prompt, so the audit-reason capture matches the app's UI.
  const [pending, setPending] = useState<{
    rider: SiSlotRider;
    next: SiConfirmationStatus;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState<SiSlotRider | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDetail(await scheduledInstantAPI.getSlot(slotId));
    } catch (e: any) {
      showErrorToast('Gagal', e.message || 'Gagal memuat detail');
    } finally {
      setLoading(false);
    }
  }, [slotId]);

  useEffect(() => {
    load();
  }, [load]);

  // A backward move (lower rank) needs an audit reason → open the modal.
  // Everything else applies immediately.
  const requestChange = (rider: SiSlotRider, next: SiConfirmationStatus) => {
    if (next === rider.confirmationStatus) return;
    if (RANK[next] < RANK[rider.confirmationStatus]) {
      setReason('');
      setPending({ rider, next });
    } else {
      applyStatus(rider, next);
    }
  };

  const applyStatus = async (
    rider: SiSlotRider,
    next: SiConfirmationStatus,
    reasonText?: string
  ) => {
    setSaving(true);
    try {
      await scheduledInstantAPI.setRiderStatus(slotId, rider.id, {
        status: next,
        reason: reasonText,
      });
      showSuccessToast(
        'Status diperbarui',
        `${rider.riderName}: ${STATUS_LABEL[next]}`
      );
      setPending(null);
      await load();
      onChanged();
    } catch (e: any) {
      showErrorToast('Gagal', e.message || 'Gagal ubah status');
    } finally {
      setSaving(false);
    }
  };

  // Manual WA reminder to one rider (bypasses the scheduled T-12/T-2 sends).
  const sendReminder = async (rider: SiSlotRider) => {
    setSendingId(rider.id);
    try {
      const res = await scheduledInstantAPI.sendRiderReminder(slotId, rider.id);
      if (res.inert) {
        showInfoToast(
          'Notifikasi WA nonaktif',
          'Aktifkan ENABLE_SI_NOTIFICATIONS di backend untuk mengirim.'
        );
      } else if (res.sent) {
        showSuccessToast(
          'Reminder terkirim',
          `WA reminder ke ${rider.riderName}`
        );
      } else {
        showErrorToast(
          'Gagal kirim',
          `WA reminder ke ${rider.riderName} gagal`
        );
      }
      await load();
    } catch (e: any) {
      showErrorToast('Gagal', e.message || 'Gagal mengirim reminder');
    } finally {
      setSendingId(null);
    }
  };

  if (loading)
    return (
      <div
        className={`text-xs text-gray-400 ${indented ? 'p-4 pl-10' : 'px-5 py-4'}`}
      >
        Memuat rider…
      </div>
    );
  if (!detail) return null;

  return (
    <div className={`space-y-4 ${indented ? 'px-4 py-4 pl-10' : 'px-5 py-4'}`}>
      {onEditSlot && (
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-gray-400">
            {dayjs(detail.slot.scheduleDate).format('dddd, DD MMM')} ·{' '}
            {detail.slot.windowStartTime}–{detail.slot.windowEndTime}
          </p>
          <Button variant="white" size="xs" onClick={() => onEditSlot(slotId)}>
            <i className="ri-edit-line mr-1" /> Edit slot hari ini
          </Button>
        </div>
      )}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">
          Rider Utama ({detail.mainRiders.length})
        </p>
        <div
          className={`grid gap-2 ${
            indented
              ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
              : 'grid-cols-1'
          }`}
        >
          {detail.mainRiders.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between bg-white border border-gray-200 rounded-gsm px-3 py-2 gap-2"
            >
              <span className="flex items-center gap-2 min-w-0">
                <Avatar name={r.riderName} />
                <span className="text-xs font-medium text-ink truncate">
                  {r.riderName}
                </span>
                {r.isDayOff && (
                  <Badge tone="warning" size="small">
                    <i className="ri-moon-line mr-0.5" />
                    Day Off
                  </Badge>
                )}
              </span>
              <span className="flex items-center gap-1 shrink-0">
                <span className="relative">
                  <select
                    value={r.confirmationStatus}
                    onChange={(e) =>
                      requestChange(r, e.target.value as SiConfirmationStatus)
                    }
                    className={`appearance-none text-[11px] font-semibold rounded-gsm-md border pl-2.5 pr-6 py-1 outline-none cursor-pointer transition-colors focus:ring-2 focus:ring-primary/30 focus:border-primary ${
                      STATUS_SELECT_TONE[STATUS_TONE[r.confirmationStatus]] ||
                      STATUS_SELECT_TONE.neutral
                    }`}
                  >
                    {STATUSES.map((st) => (
                      <option key={st} value={st} className="text-ink">
                        {STATUS_LABEL[st]}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[13px] opacity-60" />
                </span>
                <button
                  type="button"
                  onClick={() => sendReminder(r)}
                  disabled={sendingId === r.id}
                  title="Kirim reminder WhatsApp sekarang"
                  className="h-7 px-1.5 flex items-center gap-1 rounded-gsm text-[11px] font-semibold text-green-700 hover:bg-green-50 transition-colors disabled:opacity-50"
                >
                  <i className="ri-whatsapp-line" />{' '}
                  {sendingId === r.id ? 'Mengirim…' : 'Kirim WA'}
                </button>
                <button
                  type="button"
                  onClick={() => setReplaceTarget(r)}
                  title="Cari pengganti"
                  className="h-7 px-1.5 flex items-center gap-1 rounded-gsm text-[11px] font-semibold text-accent hover:bg-(--theme-accent-alpha-10) transition-colors"
                >
                  <i className="ri-user-search-line" /> Ganti
                </button>
              </span>
            </div>
          ))}
        </div>
      </div>

      {detail.reserveRiders.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">
            ⭐ Rider Cadangan ({detail.reserveRiders.length})
          </p>
          <div className="space-y-1.5">
            {detail.reserveRiders.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between bg-(--theme-accent-alpha-10) border border-(--theme-accent-alpha-16) rounded-gsm px-3 py-2"
              >
                <span className="flex items-center gap-2">
                  <Avatar name={r.riderName} reserve />
                  <span className="text-xs font-medium text-ink">
                    {r.riderName}
                  </span>
                  {r.isDayOff && (
                    <Badge tone="warning" size="small">
                      <i className="ri-moon-line mr-0.5" />
                      Day Off
                    </Badge>
                  )}
                </span>
                <span className="flex items-center gap-1">
                  <Button
                    variant="outline-success"
                    size="xs"
                    disabled={sendingId === r.id}
                    onClick={() => sendReminder(r)}
                  >
                    <i className="ri-whatsapp-line mr-1" />
                    {sendingId === r.id ? 'Mengirim…' : 'Kirim WA'}
                  </Button>
                  <Button variant="outline-accent" size="xs">
                    <i className="ri-phone-line mr-1" /> Hubungi
                  </Button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail.activity.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">
            Aktivitas Slot · audit
          </p>
          <ul className="space-y-2 border-l-2 border-gray-200 pl-3">
            {detail.activity.slice(0, 8).map((a) => (
              <li key={a.id} className="text-[11px] relative">
                <span className="absolute -left-[15px] top-1 w-2 h-2 rounded-full bg-primary" />
                <span className="font-semibold text-ink">
                  {a.reason || `${a.action} ${a.toStatus ?? ''}`.trim()}
                </span>
                <span className="text-gray-400">
                  {' '}
                  · {dayjs(a.createdAt).format('DD MMM HH:mm')} · {a.actor}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Modal
        isOpen={!!pending}
        onClose={() => !saving && setPending(null)}
        icon="ri-error-warning-line"
        iconTone="warning"
        width="sm"
        title="Alasan perubahan status"
        subtitle={
          pending
            ? `${STATUS_LABEL[pending.rider.confirmationStatus]} → ${STATUS_LABEL[pending.next]} · mundur, wajib dicatat`
            : undefined
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="white"
              size="small"
              onClick={() => setPending(null)}
              disabled={saving}
            >
              Batal
            </Button>
            <Button
              variant="primary"
              size="small"
              disabled={!reason.trim() || saving}
              onClick={() =>
                pending &&
                applyStatus(pending.rider, pending.next, reason.trim())
              }
            >
              {saving ? 'Menyimpan…' : 'Simpan alasan'}
            </Button>
          </div>
        }
      >
        <div className="space-y-2">
          <p className="text-xs text-gray-500 leading-relaxed">
            Perubahan status mundur untuk{' '}
            <b className="text-ink">{pending?.rider.riderName}</b> memerlukan
            alasan (tersimpan ke audit log).
          </p>
          <textarea
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="cth. Rider konfirmasi ulang via telepon"
            className="w-full rounded-gsm border border-gray-300 px-3 py-2 text-sm text-ink outline-none resize-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>
      </Modal>

      <StandbyModal
        isOpen={!!replaceTarget}
        onClose={() => setReplaceTarget(null)}
        slotId={slotId}
        slotLabel={
          detail
            ? `${detail.slot.clientName} · ${dayjs(
                detail.slot.scheduleDate
              ).format(
                'DD MMM'
              )} · ${detail.slot.windowStartTime}–${detail.slot.windowEndTime}`
            : ''
        }
        target={replaceTarget}
        reserves={detail?.reserveRiders ?? []}
        onReplaced={(fresh) => {
          setDetail(fresh);
          setReplaceTarget(null);
          onChanged();
        }}
      />
    </div>
  );
};

// ─── Master Jadwal ──────────────────────────────────────────────────────

const rosterInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

/** Stacked avatar preview for a roster role (Master Jadwal columns). */
const RosterStack: React.FC<{
  preview: { riderCode: string; riderName: string }[];
  total: number;
  reserve?: boolean;
}> = ({ preview, total, reserve }) => {
  if (total === 0) {
    return (
      <span className="text-[11px] text-gray-400 italic">
        — belum ditetapkan
      </span>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <div className="flex -space-x-1.5">
        {preview.map((r) => (
          <span
            key={r.riderCode}
            title={r.riderName}
            className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold ring-2 ring-white ${
              reserve
                ? 'bg-(--theme-accent-alpha-10) text-accent'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {rosterInitials(r.riderName)}
          </span>
        ))}
      </div>
      {total > preview.length && (
        <span className="text-[10.5px] text-gray-400">
          +{total - preview.length} ({total})
        </span>
      )}
    </div>
  );
};

type ScheduleFilter = 'all' | 'active' | 'inactive';
const SCHEDULE_FILTERS = ['all', 'active', 'inactive'] as const;

const MasterJadwal: React.FC = () => {
  const [schedules, setSchedules] = useState<SiSchedule[]>([]);
  const [rosters, setRosters] = useState<
    Record<string, SiScheduleRosterSummary>
  >({});
  const [filter, setFilter] = useUrlParam<ScheduleFilter>('status', 'all', {
    allowed: SCHEDULE_FILTERS,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<SiSchedule | null>(null);
  const [deactivating, setDeactivating] = useState<SiSchedule | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [editSlotId, setEditSlotId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { schedules: list, rosterSummaries } =
        await scheduledInstantAPI.listSchedules();
      setSchedules(list);
      setRosters(
        Object.fromEntries(rosterSummaries.map((r) => [r.scheduleId, r]))
      );
    } catch (e: any) {
      setError(e.message || 'Gagal memuat jadwal');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reactivate = async (s: SiSchedule) => {
    try {
      await scheduledInstantAPI.reactivateSchedule(s.id);
      showSuccessToast('Jadwal diaktifkan', s.clientName);
      load();
    } catch (e: any) {
      showErrorToast('Gagal', e.message || 'Gagal mengaktifkan');
    }
  };

  const counts = {
    all: schedules.length,
    active: schedules.filter((s) => s.isActive).length,
    inactive: schedules.filter((s) => !s.isActive).length,
  };
  const filtered = schedules.filter((s) =>
    filter === 'all' ? true : filter === 'active' ? s.isActive : !s.isActive
  );

  // Group schedules by client so a client with >1 slot renders under one
  // header with its slots nested (Goreca = 1 slot → flat; Zalora = 2 → grouped).
  const groups = React.useMemo(() => {
    const map = new Map<string, SiSchedule[]>();
    for (const s of filtered) {
      const list = map.get(s.clientName) ?? [];
      list.push(s);
      map.set(s.clientName, list);
    }
    return Array.from(map, ([clientName, items]) => ({ clientName, items }));
  }, [filtered]);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-500">
          Jadwal berulang — men-generate slot harian otomatis.
        </p>
        <Button
          variant="primary"
          size="small"
          onClick={() => setShowCreate(true)}
        >
          <i className="ri-add-line mr-1" /> Buat Jadwal Baru
        </Button>
      </div>

      {/* Filter tabs (Screen 7) */}
      <div className="flex items-center gap-1 mb-3">
        {(
          [
            { key: 'all', label: `Semua (${counts.all})` },
            { key: 'active', label: `🟢 Aktif (${counts.active})` },
            { key: 'inactive', label: `⚪ Non-Aktif (${counts.inactive})` },
          ] as { key: ScheduleFilter; label: string }[]
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 rounded-gsm text-[11px] font-semibold transition-colors ${
              filter === t.key
                ? 'bg-(--theme-accent-alpha-10) text-accent'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <SkeletonBlock />}

      {!loading && error && (
        <Card className="border-red-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-red-700">{error}</p>
            <Button variant="outline-danger" size="small" onClick={load}>
              Coba lagi
            </Button>
          </div>
        </Card>
      )}

      {!loading && !error && filtered.length === 0 && (
        <Card>
          <p className="text-sm text-gray-500 text-center py-8">
            {schedules.length === 0
              ? 'Belum ada jadwal berulang. Klik “Buat Jadwal Baru” untuk mulai.'
              : 'Tidak ada jadwal pada filter ini.'}
          </p>
        </Card>
      )}

      {!loading && !error && filtered.length > 0 && (
        <Panel>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                  <th className="text-left px-4 py-2.5 font-semibold">Klien</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Jam</th>
                  <th className="text-left px-4 py-2.5 font-semibold">
                    Rider Utama
                  </th>
                  <th className="text-left px-4 py-2.5 font-semibold">
                    Rider Cadangan
                  </th>
                  <th className="text-left px-4 py-2.5 font-semibold">
                    Toleransi
                  </th>
                  <th className="text-left px-4 py-2.5 font-semibold">
                    Status
                  </th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {groups.flatMap((g) => {
                  const nested = g.items.length > 1;
                  const header = nested ? (
                    <tr
                      key={`grp-${g.clientName}`}
                      className="bg-gray-50 border-b border-gray-100"
                    >
                      <td colSpan={7} className="px-4 py-2.5">
                        <span className="font-bold text-ink">
                          {g.clientName}
                        </span>
                        <span className="ml-2 inline-flex items-center rounded-gsm-sm bg-(--theme-accent-alpha-10) px-2 py-0.5 text-[10px] font-semibold text-accent">
                          {g.items.length} Jadwal
                        </span>
                      </td>
                    </tr>
                  ) : null;
                  const rows = g.items.map((s) => {
                    const rs = rosters[s.id];
                    return (
                      <tr
                        key={s.id}
                        className={`border-b border-gray-50 hover:bg-gray-50 ${
                          s.isActive ? '' : 'opacity-60'
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div
                            className={
                              nested
                                ? 'pl-3 border-l-2 border-gray-200'
                                : undefined
                            }
                          >
                            <span className="font-semibold text-ink">
                              {nested ? s.batchLabel || 'Slot' : s.clientName}
                            </span>
                            {!nested && s.batchLabel && (
                              <span className="text-gray-400 text-xs">
                                {' '}
                                · {s.batchLabel}
                              </span>
                            )}
                            <div className="text-[10.5px] text-gray-400 mt-0.5">
                              {s.recurringWeekdays
                                .map((d) => DAY_LABELS[d - 1])
                                .join(', ')}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 tabular text-gray-600 whitespace-nowrap">
                          {s.windowStartTime}–{s.windowEndTime}
                        </td>
                        <td className="px-4 py-3">
                          <RosterStack
                            preview={rs?.utamaPreview ?? []}
                            total={rs?.utamaCount ?? 0}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <RosterStack
                            preview={rs?.cadanganPreview ?? []}
                            total={rs?.cadanganCount ?? 0}
                            reserve
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone="danger" size="small">
                            Zero
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={s.isActive ? 'success' : 'neutral'} dot>
                            {s.isActive ? 'Aktif' : 'Non-Aktif'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="outline-accent"
                              size="xs"
                              onClick={() => setDetailId(s.id)}
                            >
                              Lihat Detail
                            </Button>
                            {s.isActive ? (
                              <Button
                                variant="ghost"
                                size="xs"
                                onClick={() => setDeactivating(s)}
                              >
                                Nonaktifkan
                              </Button>
                            ) : (
                              <Button
                                variant="outline-success"
                                size="xs"
                                onClick={() => reactivate(s)}
                              >
                                Aktifkan
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  });
                  return header ? [header, ...rows] : rows;
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <CreateScheduleModal
        isOpen={showCreate || !!editing}
        editing={editing}
        onClose={() => {
          setShowCreate(false);
          setEditing(null);
        }}
        onCreated={load}
      />

      <DeactivateScheduleModal
        isOpen={!!deactivating}
        schedule={deactivating}
        onClose={() => setDeactivating(null)}
        onDone={load}
      />

      <ScheduleDetailDrawer
        scheduleId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={load}
        onEditSchedule={(s) => {
          setDetailId(null);
          setEditing(s);
        }}
        onDeactivate={(s) => {
          setDetailId(null);
          setDeactivating(s);
        }}
        onEditSlot={(id) => setEditSlotId(id)}
      />

      <EditSlotModal
        slotId={editSlotId}
        onClose={() => setEditSlotId(null)}
        onSaved={load}
      />
    </div>
  );
};

// ─── Day Off ────────────────────────────────────────────────────────────

const DayOff: React.FC = () => {
  const [dayOffs, setDayOffs] = useState<SiRiderDayOff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ code: string; name: string } | null>(
    null
  );
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Names come resolved on the row (riderName) — no per-rider directory
      // lookups (previously an N+1 of /v3/drivers, one call per day-off).
      const list = await scheduledInstantAPI.listDayOffs();
      setDayOffs(list);
    } catch (e: any) {
      setError(e.message || 'Gagal memuat day-off');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-500 max-w-2xl">
          Rider dengan hari libur aktif (rutin atau tanggal spesifik) tidak
          disarankan sistem untuk slot baru atau standby. Ops tetap bisa
          menugaskan manual dengan konfirmasi.
        </p>
        <Button variant="primary" size="small" onClick={() => setShowAdd(true)}>
          <i className="ri-add-line mr-1" /> Tambah Rider
        </Button>
      </div>

      {loading && <SkeletonBlock />}

      {!loading && error && (
        <Card className="border-red-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-red-700">{error}</p>
            <Button variant="outline-danger" size="small" onClick={load}>
              Coba lagi
            </Button>
          </div>
        </Card>
      )}

      {!loading && !error && dayOffs.length === 0 && (
        <Card>
          <p className="text-sm text-gray-500 text-center py-8">
            Belum ada day-off. Klik “Tambah Rider” untuk mengatur.
          </p>
        </Card>
      )}

      {!loading && !error && dayOffs.length > 0 && (
        <Panel>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="text-left px-4 py-2.5 font-semibold">Rider</th>
                <th className="text-left px-4 py-2.5 font-semibold">
                  Hari Libur Rutin
                </th>
                <th className="text-left px-4 py-2.5 font-semibold">
                  Tanggal Off Spesifik
                </th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {dayOffs.map((d) => {
                const rec = d.recurringWeekdays ?? [];
                const name = d.riderName || d.riderCode;
                return (
                  <tr
                    key={d.riderCode}
                    className="border-b border-gray-50 hover:bg-gray-50"
                  >
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        <Avatar name={name} />
                        <span className="min-w-0">
                          <span className="block text-xs font-semibold text-ink truncate">
                            {name}
                          </span>
                          <span className="block font-mono text-[10px] text-gray-400">
                            {d.riderCode}
                          </span>
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {DAY_LABELS.map((label, i) => {
                          const active = rec.includes(i + 1);
                          return (
                            <span
                              key={i}
                              className={`px-1.5 py-0.5 rounded-gsm-sm text-[10px] font-semibold border ${
                                active
                                  ? 'bg-red-50 border-red-200 text-red-700'
                                  : 'bg-white border-gray-200 text-gray-300'
                              }`}
                            >
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(d.specificDates?.length ?? 0) > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {d.specificDates!.map((dt) => (
                            <span
                              key={dt}
                              className="font-mono text-[11px] bg-tint border border-gray-200 rounded-gsm px-1.5 py-0.5 text-ink"
                            >
                              {dt}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">
                          — tidak ada
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline-accent"
                        size="xs"
                        onClick={() => setEditing({ code: d.riderCode, name })}
                      >
                        Atur
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
      )}

      <DayOffEditorModal
        isOpen={showAdd || !!editing}
        rider={editing}
        onClose={() => {
          setShowAdd(false);
          setEditing(null);
        }}
        onSaved={load}
      />
    </div>
  );
};

export default ScheduledInstantPage;
